import React, { useState, useEffect } from 'react';
import { ShoppingBag, Search, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';

import { SearchDrawer } from './SearchDrawer';

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { toggleCart, count } = useCart();
  const location = useLocation();

  // Pages with Dark Hero sections (White Text needed)
  const isDarkHero = location.pathname.startsWith('/product/') || 
                     location.pathname === '/our-story' || 
                     location.pathname === '/shop' ||
                     location.pathname === '/journal' ||
                     location.pathname === '/contact';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop', path: '/shop' },
    { name: 'Our Story', path: '/our-story' },
    { name: 'Journal', path: '/journal' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <>
      <SearchDrawer isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <nav 
        className={`fixed w-full z-50 transition-all duration-500 ease-out ${
          scrolled 
            ? 'bg-simba-cream/80 backdrop-blur-md border-b border-simba-green/10 py-4 shadow-sm' 
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center">
            
            {/* Left: Mobile Menu Trigger (Visible on Mobile) */}
            <div className="lg:hidden">
              <button 
                onClick={() => setMobileMenuOpen(true)}
                className={`transition-colors ${
                  !scrolled && isDarkHero ? 'text-white hover:text-simba-gold' : 'text-simba-green hover:text-simba-gold'
                }`}
              >
                <Menu size={28} strokeWidth={1.5} />
              </button>
            </div>

            {/* Logo - Centered on Mobile, Left on Desktop */}
            <div className={`flex-shrink-0 flex items-center transition-transform duration-500 ${scrolled ? 'scale-90' : 'scale-100'}`}>
              <Link to="/" className="group block">
                <img 
                  src="/logo/simba-HERBALS-Logo_hres-1024x321.webp" 
                  alt="Simba Herbals" 
                  className={`h-12 w-auto object-contain transition-all duration-300 ${
                    !scrolled && isDarkHero ? 'brightness-0 invert' : ''
                  }`}
                />
              </Link>
            </div>

            {/* Desktop Menu - Centered */}
            <div className="hidden lg:flex items-center space-x-12">
              {navLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`font-body font-medium text-sm uppercase tracking-widest transition-all hover:tracking-[0.15em] ${
                    !scrolled && isDarkHero 
                      ? 'text-white/90 hover:text-simba-gold' 
                      : 'text-simba-green/80 hover:text-simba-gold'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => setSearchOpen(true)}
                className={`transition-colors hidden sm:block ${
                  !scrolled && isDarkHero ? 'text-white hover:text-simba-gold' : 'text-simba-green hover:text-simba-gold'
                }`}
              >
                <Search size={22} strokeWidth={1.5} />
              </button>
              <button onClick={toggleCart} className="relative group cursor-pointer">
                {count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-simba-gold text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full group-hover:scale-110 transition-transform">
                    {count}
                  </span>
                )}
                <ShoppingBag 
                  size={22} 
                  strokeWidth={1.5} 
                  className={`transition-colors ${
                    !scrolled && isDarkHero ? 'text-white group-hover:text-simba-gold' : 'text-simba-green group-hover:text-simba-gold'
                  }`} 
                />
              </button>
            </div>

          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-[60] bg-simba-green transform transition-transform duration-500 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex justify-end p-8">
          <button onClick={() => setMobileMenuOpen(false)} className="text-simba-lime hover:text-white transition-colors">
            <X size={32} strokeWidth={1} />
          </button>
        </div>
        <div className="flex flex-col items-center justify-center h-full space-y-8 -mt-20">
          {navLinks.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className="font-display text-4xl text-simba-lime hover:text-white transition-colors"
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};
